import type { ColumnEntry } from "@/v2/framework/column/types";
import type { DynamicColumnSlot } from "@/v2/framework/view/types";
import { FieldType, ColumnIconType } from "@/v2/framework/column/types";
import { categoryGamesStatusOptions } from "../shared";
import { t } from "@/services/i18n";
import { getSupplementalsByEntity } from "@/services/supplementals";

export const columns: ColumnEntry[] = [
  { key: "id", defaultVisibility: true, title: "table_columns.id", fieldType: FieldType.TextCell },
  {
    key: "syntheticId",
    defaultVisibility: false,
    title: "table_columns.synthetic_id",
    fieldType: FieldType.TextCell,
  },
  {
    key: "sportName",
    defaultVisibility: true,
    title: "table_columns.sport",
    fieldType: FieldType.TextCell,
    sort: {
      enabled: true,
      key: "sportName",
    },
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => {
        const item = row as { sportCode: string };
        return { code: `${item.sportCode}` };
      },
    },
  },

  {
    key: "sourceTenantName",
    defaultVisibility: true,
    title: "table_columns.source_tenant_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "name",
    defaultVisibility: true,
    title: "table_columns.name",
    fieldType: FieldType.InputCell,
    sort: {
      enabled: true,
      key: "name",
    },
  },
  {
    key: "sourceName",
    defaultVisibility: true,
    title: "table_columns.source_name",
    fieldType: FieldType.TextCell,
  },
  {
    type: "group",
    key: "prematch",
    title: "table_columns.prematch_group",
    columns: [
      {
        key: "prematchGameStatus",
        defaultVisibility: true,
        title: "table_columns.prematch_status",
        fieldType: FieldType.TextCell,
        valueFormatter: (row: unknown) => {
          const item = row as { prematchGame: { status: number } };
          const gameStatus = categoryGamesStatusOptions.find(
            (option) => option.value === item.prematchGame.status,
          );
          return t(gameStatus?.title ?? "");
        },
        icon: {
          type: ColumnIconType.Prepend,
          formatter: (row: unknown) => {
            const item = row as { prematchGame: { status: number } };
            const gameStatus = categoryGamesStatusOptions.find(
              (option) => option.value === item.prematchGame.status,
            );
            return {
              code: gameStatus?.icon?.code ?? "",
              color: gameStatus?.icon?.color ?? "",
            };
          },
        },
      },
      {
        key: "prematchGamePosition",
        defaultVisibility: true,
        title: "table_columns.prematch_position",
        fieldType: FieldType.InputCell,
        apiPath: "prematchGame.position",
        valueFormatter: (row: unknown) => {
          const item = row as { prematchGame: { position: number } };
          return item.prematchGame.position.toString();
        },
      },
    ],
  },
  {
    type: "group",
    key: "live",
    title: "table_columns.live_group",
    columns: [
      {
        key: "liveGameStatus",
        defaultVisibility: true,
        title: "table_columns.live_status",
        fieldType: FieldType.TextCell,
        valueFormatter: (row: unknown) => {
          const item = row as { liveGame: { status: number } };
          const gameStatus = categoryGamesStatusOptions.find(
            (option) => option.value === item.liveGame.status,
          );
          return t(gameStatus?.title ?? "");
        },
        icon: {
          type: ColumnIconType.Prepend,
          formatter: (row: unknown) => {
            const item = row as { liveGame: { status: number } };
            const gameStatus = categoryGamesStatusOptions.find(
              (option) => option.value === item.liveGame.status,
            );
            return {
              code: gameStatus?.icon?.code ?? "",
              color: gameStatus?.icon?.color ?? "",
            };
          },
        },
      },
      {
        key: "liveGamePosition",
        defaultVisibility: true,
        title: "table_columns.live_position",
        fieldType: FieldType.InputCell,
        apiPath: "liveGame.position",
        valueFormatter: (row: unknown) => {
          const item = row as { liveGame: { position: number } };
          return item.liveGame.position.toString();
        },
      },
    ],
  },
  {
    key: "feeds",
    defaultVisibility: true,
    title: "table_columns.feeds",
    fieldType: FieldType.ArrayCell,
    valueFormatter: (row: unknown) => {
      const item = row as { feeds: { name: string }[] };
      return item.feeds.map((feed) => {
        const [name] = feed.name?.split(" - ") ?? [];
        return name ?? "";
      });
    },
  },
  {
    key: "gridChrome",
    defaultVisibility: true,
    configurableVisibility: false,
    title: "",
    fieldType: FieldType.ActionsCell,
    sticky: true,
    columnsPanelTitle: "common.labels.column_view",
    headerActions: [
      {
        icon: "table-d",
        label: "Column view",
      },
    ],
    rowActions: [
      {
        icon: "edit-ng",
        label: "Edit",
        onClick: (ctx) => ctx.openDialog("edit"),
      },
      {
        icon: "trash-ng",
        label: "Delete",
        onClick: (ctx) => ctx.openDialog("delete"),
      },
    ],
  },
];

export const dynamicColumnSlots: DynamicColumnSlot[] = [
  {
    anchor: "name",
    createColumns: () => {
      const supplementalNameTypes = getSupplementalsByEntity("CATEGORY");

      return supplementalNameTypes.map((item) => ({
        key: item.id,
        title: `table_columns.${item.name.toLowerCase()}`,
        fieldType: FieldType.InputCell,
        defaultVisibility: true,
        valueFormatter: (row: unknown) => {
          const r = row as {
            supplementalNames: { supplementalNameTypeId: string; name: string }[];
          };
          return r.supplementalNames.find((s) => s.supplementalNameTypeId === item.id)?.name ?? "";
        },
        payloadOutFormatter: (value: unknown) => ({
          supplementalNames: [{ supplementalNameTypeId: item.id, name: value }],
        }),
      }));
    },
  },
];
